import { useState, useEffect } from 'react';
import axios from 'axios';
import AdminLayout from '../../components/admin/AdminLayout';
import { Plus, Edit, Trash2, Search, X, Upload } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import { useAuth } from '../../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMenu() {
  const { token } = useAuth();
  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [uploading, setUploading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    price: '',
    category_id: '',
    subcategory_id: '',
    image_url: '',
    is_available: true,
    is_featured: false,
    tags: []
  });

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchData = async () => {
    try {
      const [menuRes, catRes, subRes] = await Promise.all([
        axios.get(`${API}/menu/all`, { headers }),
        axios.get(`${API}/categories`),
        axios.get(`${API}/subcategories`)
      ]);
      setMenuItems(menuRes.data);
      setCategories(catRes.data);
      setSubcategories(subRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter subcategories based on selected category
  const filteredSubcategories = subcategories.filter(s => s.category_id === formData.category_id);

  // Upload image handler
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const uploadFormData = new FormData();
    uploadFormData.append('file', file);
    
    try {
      const res = await axios.post(`${API}/media/upload`, uploadFormData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });
      setFormData({ ...formData, image_url: `${API}/media/files/${res.data.path}` });
      toast.success('Image uploaded');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      description: '',
      price: '',
      category_id: categories[0]?.id || '',
      subcategory_id: '',
      image_url: '',
      is_available: true,
      is_featured: false,
      tags: []
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      description: item.description,
      price: item.price.toString(),
      category_id: item.category_id,
      subcategory_id: item.subcategory_id || '',
      image_url: item.image_url || '',
      is_available: item.is_available,
      is_featured: item.is_featured,
      tags: item.tags || []
    });
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const data = {
      ...formData,
      price: parseFloat(formData.price),
      subcategory_id: formData.subcategory_id || null
    };

    try {
      if (editingItem) {
        await axios.put(`${API}/menu/${editingItem.id}`, data, { headers });
        toast.success('Menu item updated');
      } else {
        await axios.post(`${API}/menu`, data, { headers });
        toast.success('Menu item created');
      }
      setIsDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving menu item:', error);
      toast.error('Failed to save menu item');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await axios.delete(`${API}/menu/${id}`, { headers });
      toast.success('Menu item deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting menu item:', error);
      toast.error('Failed to delete menu item');
    }
  };

  const toggleAvailability = async (item) => {
    try {
      await axios.put(`${API}/menu/${item.id}`, { is_available: !item.is_available }, { headers });
      fetchData();
    } catch (error) {
      console.error('Error toggling availability:', error);
    }
  };

  const filteredItems = menuItems.filter(item => {
    const matchesCategory = activeTab === 'all' || item.category_id === activeTab;
    const matchesSearch = item.name.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const getCategoryName = (categoryId) => {
    return categories.find(c => c.id === categoryId)?.name || 'Unknown';
  };

  if (loading) {
    return (
      <AdminLayout title="Menu Management" subtitle="Manage your delicious menu items, prices, and availability.">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-fire-500"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Menu Management" subtitle="Manage your delicious menu items, prices, and availability.">
      <div data-testid="admin-menu">
        {/* Tabs and Actions */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="overflow-x-auto -mx-3 px-3 pb-2">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-max min-w-full">
              <TabsList className="bg-card border border-white/10 flex-nowrap">
                <TabsTrigger value="all" className="data-[state=active]:bg-fire-500 text-xs sm:text-sm whitespace-nowrap">All</TabsTrigger>
                {categories.map(cat => (
                  <TabsTrigger key={cat.id} value={cat.id} className="data-[state=active]:bg-fire-500 text-xs sm:text-sm whitespace-nowrap">
                    {cat.name}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
          </div>
          
          <div className="flex gap-2 sm:gap-4">
            <div className="relative flex-1 sm:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
              <Input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 bg-card border-white/10 text-white w-full sm:w-48"
              />
            </div>
            <Button onClick={openCreateDialog} className="bg-fire-500 hover:bg-fire-600 flex-shrink-0" data-testid="add-menu-item-btn">
              <Plus className="w-4 h-4 sm:mr-2" /><span className="hidden sm:inline">Add Item</span>
            </Button>
          </div>
        </div>

        {/* Menu Items Grid */}
        <div className="space-y-3">
          {filteredItems.map(item => (
            <div
              key={item.id}
              className="bg-card rounded-xl border border-white/10 p-3 sm:p-4 flex flex-col sm:flex-row gap-3 sm:gap-4"
              data-testid={`menu-item-row-${item.id}`}
            >
              <div className="w-full sm:w-24 h-32 sm:h-24 rounded-xl overflow-hidden flex-shrink-0">
                <img src={item.image_url || 'https://via.placeholder.com/100'} alt={item.name} className="w-full h-full object-cover" />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-white font-bold text-base sm:text-lg flex items-center gap-2 flex-wrap">
                      <span className="truncate">{item.name}</span>
                      <Switch
                        checked={item.is_available}
                        onCheckedChange={() => toggleAvailability(item)}
                        className="data-[state=checked]:bg-green-500 flex-shrink-0"
                      />
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2">{item.description}</p>
                    <div className="flex flex-wrap items-center gap-2 mt-2">
                      <span className="text-fire-500 font-bold">₦{item.price.toLocaleString()}</span>
                      <span className="text-gray-500 text-xs">• {getCategoryName(item.category_id)}</span>
                      {item.is_featured && (
                        <span className="bg-fire-500/20 text-fire-500 text-xs px-2 py-1 rounded-full">Featured</span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-1 flex-shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => openEditDialog(item)} className="text-gray-400 hover:text-white h-8 w-8 p-0" data-testid={`edit-item-${item.id}`}><Edit className="w-4 h-4" /></Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item.id)} className="text-red-400 hover:text-red-500 h-8 w-8 p-0" data-testid={`delete-item-${item.id}`}><Trash2 className="w-4 h-4" /></Button>
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              No menu items found
            </div>
          )}
        </div>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="bg-card border-white/10 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white">
                {editingItem ? 'Edit Menu Item' : 'Add Menu Item'}
              </DialogTitle>
            </DialogHeader>
            
            <form onSubmit={handleSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div>
                <Label className="text-gray-400">Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="menu-form-name"
                />
              </div>
              
              <div>
                <Label className="text-gray-400">Description</Label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  required
                  className="bg-black/50 border-white/10 text-white"
                  data-testid="menu-form-description"
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400">Price (₦)</Label>
                  <Input
                    type="number"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    required
                    className="bg-black/50 border-white/10 text-white"
                    data-testid="menu-form-price"
                  />
                </div>
                <div>
                  <Label className="text-gray-400">Category</Label>
                  <Select value={formData.category_id} onValueChange={(v) => setFormData({ ...formData, category_id: v, subcategory_id: '' })}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Subcategory Selection */}
              {filteredSubcategories.length > 0 && (
                <div>
                  <Label className="text-gray-400">Subcategory (Optional)</Label>
                  <Select value={formData.subcategory_id} onValueChange={(v) => setFormData({ ...formData, subcategory_id: v })}>
                    <SelectTrigger className="bg-black/50 border-white/10 text-white">
                      <SelectValue placeholder="Select subcategory" />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-white/10">
                      <SelectItem value="">None (Direct under category)</SelectItem>
                      {filteredSubcategories.map(sub => (
                        <SelectItem key={sub.id} value={sub.id}>{sub.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              
              <div>
                <Label className="text-gray-400">Image</Label>
                <div className="flex gap-2">
                  <Input
                    value={formData.image_url}
                    onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                    className="bg-black/50 border-white/10 text-white flex-1"
                    placeholder="https://... or upload"
                    data-testid="menu-form-image"
                  />
                  <label className="cursor-pointer">
                    <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                    <Button type="button" variant="outline" className="border-white/10 text-white" disabled={uploading}>
                      {uploading ? '...' : <Upload className="w-4 h-4" />}
                    </Button>
                  </label>
                </div>
                {formData.image_url && (
                  <div className="mt-2">
                    <img src={formData.image_url} alt="Preview" className="h-20 rounded-lg object-cover" />
                  </div>
                )}
              </div>
              
              <div className="flex gap-6">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_available}
                    onCheckedChange={(v) => setFormData({ ...formData, is_available: v })}
                    className="data-[state=checked]:bg-green-500"
                  />
                  <Label className="text-gray-400">Available</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.is_featured}
                    onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                    className="data-[state=checked]:bg-fire-500"
                  />
                  <Label className="text-gray-400">Featured</Label>
                </div>
              </div>
              
              <div className="flex gap-4 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="flex-1 border-white/10 text-white">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-fire-500 hover:bg-fire-600" data-testid="menu-form-submit">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
